import React from 'react'

import Dialog from '../DialogWrapper'
import { flow } from 'lodash'
import { observer } from 'mobx-react'
import { IDialog } from '../../store/models/Dialog'
import { createStyles, DialogTitle, Grid, makeStyles, Theme, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import ShareStory from './ShareStory'
import { MediaFilePreview } from '../utils/AvatarUploader'
import StoryShow from './StoryShow'

interface Props {
  dialog: IDialog
}

/**
 * The post-submit success modal -- what a guest sees straight after sending a
 * story, having usually arrived by scanning the QR code at a dinner.
 *
 * The grey band used to open with an Inclusivv-era megaphone graphic hardcoded
 * against `civicdinners-manual-storage.s3.amazonaws.com` -- a third party's
 * personal S3 account this platform is actively migrating off. That art was
 * both off-brand for the theatre and a live read from an account that can
 * disappear. Same call as the three images removed from StoryCollection.tsx:
 * there is no OOH replacement, so it is removed rather than re-pointed.
 *
 * Nothing else about the band changed. `subHeaderContainer` keeps its
 * `minHeight: 10em`, so it holds its shape around the heading and the share
 * row instead of collapsing. Adding OOH's own artwork here is separate work.
 */
const SuccessDialog = ({ dialog }: Props) => {
  const { t } = useTranslation()
  const classes = Styles()
  const { conversation, headline, story, file } = dialog.data as any

  /**
   * Whether this story is waiting for a moderator rather than live.
   *
   * Read off `visible` on the record the SERVER just returned, not inferred
   * from the route or from whether anyone is signed in: the server is what
   * decides, stamping `visible: false` on every submission that presents a
   * card token. A story filed the old way comes back visible and nothing here
   * changes for it.
   */
  const pendingApproval = story?.visible === false

  return (
    <Dialog dialog={dialog} name='storyCollectionSuccess' type='noPadding' closeIcon={true} closeClass={classes.closeIcon}>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant='subtitle1'>{t('story.thankYou')}</Typography>
      </DialogTitle>
      <Grid container direction='column'>
        <Grid item>
          <div className={classes.subHeaderContainer}>
            {/*
              * 🔴 The standing header is "Amplify your impact by sharing your
              * story with friends on social media". For a story awaiting
              * approval that is not merely off-tone, it is false twice over:
              * the story is not on the site yet, and the link the share
              * buttons hand out does not work.
              *
              * The share URL is `Story.storyLink` -> `/{slug}/a/stories/{id}`,
              * which the topic page resolves out of `topic.topicStories`, and
              * that list is loaded through `Api::Topics::StoriesController`
              * with `only_visible: true`. An unapproved story is not in it, so
              * anyone following the link lands on the topic page with nothing
              * shown. Telling a stranger to post that to Facebook is the worst
              * version of this dialog.
              *
              * So a pending story gets copy that says what actually happened,
              * and no share row at all. Both come back by themselves once a
              * moderator approves it and the story is genuinely shareable.
              */}
            <Typography variant='h6' className={classes.successHeader} align='center'>
              {pendingApproval ? t('story.successHeaderPending') : t('story.successHeader')}
            </Typography>
            {!pendingApproval && (
              <ShareStory story={story} color='#898A8E' noLabel={true} spacing={2} className={classes.shareStory} justify={'center'} hideShareLabel={true} />
            )}
          </div>
        </Grid>
        <StoryShow story={story} />
      </Grid>
    </Dialog>
  )
}

const Styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      textAlign: 'center',
      margin: '0 auto',
      width: '100%',
      backgroundColor: '#E5E5E5'
    },
    dialogTitle: { backgroundColor: '#ffffff', color: '#000000' },
    subHeaderContainer: {
      textAlign: 'center',
      margin: '0 auto',
      backgroundColor: '#F6F7F9',
      minHeight: '10em',
      padding: '1em 0'
    },
    successHeader: {
      textAlign: 'center',
      margin: '.4em auto',
      maxWidth: '18em'
    },
    content: {
      margin: '1em auto',
      padding: '1em',
      maxWidth: '25em'
    },

    shareStory: {
      maxWidth: '15em',
      margin: '0 auto'
    },
    shareContainerClass: {
      backgroundColor: '#ff0000'
    },

    image: {
      borderRadius: '1.3em'
    },
    closeIcon: {
      color: '#818285'
    },
    spacer: {
      marginTop: '1em'
    },
    shareContainer: {}
  })
)

export default flow(observer)(SuccessDialog)
